<template>
  <PageDynamic :path="pagePath" :title="pageTitle" :description="pageDescription">

    <h1 v-if="h1Text" class="sr-only">{{ h1Text }}</h1>

    <div v-if="pageContent" v-html="pageContent"></div>

  </PageDynamic>

</template>

<script setup>

import { ref, computed, onMounted } from "vue";
import { useRoute } from "vue-router";
import { usePageContentStore } from "@/stores/pageContent";
import { useShortcodeParser } from "~/composables/useShortcodeParser";
import { useBreadcrumbSchema } from "~/composables/useBreadcrumbSchema";
import PageDynamic from "@/components/pages/Dynamic.vue";

const store = usePageContentStore();
const route = useRoute();
const { parseShortcodes } = useShortcodeParser();
const { public: { siteUrl } } = useRuntimeConfig();

// name/serviceType/description sourced from each service page's own SEO
// audit ticket (AI-summarized from the page's real content, grounding-checked
// against what's actually rendered) — keyed by the URL's last slug segment.
const SERVICE_DATA = {
  "communication-materials": {
    name: "Communication Materials Production Services",
    alternateName: "Communication Materials",
    description: "Our services help you create impactful reports and materials that effectively convey your message and leave a lasting impression.",
  },
  "corporate-website": {
    name: "Corporate Website",
    description: "Corporate website design and development services tailored to meet the unique needs of businesses across various industries.",
  },
  "cyber-security": {
    name: "Vulnerability Assessment and Penetration Testing Services",
    alternateName: "Cyber Security",
    description: "ShareInvestor (Thailand) offers vulnerability assessment and penetration testing services to identify and mitigate security risks.",
  },
  "digital-annual-report": {
    name: "Digital Annual Report",
    description: "ShareInvestor Thailand's Digital Annual Report service is designed to transform your corporate communication and enhance your investor relations strategy.",
  },
  "esg-microsite": {
    name: "ESG Microsite Service",
    alternateName: "Esg Microsite",
    description: "The FTSE Russell ESG Microsite Service from ShareInvestor (Thailand) is designed to help investor relations or sustainability teams communicate their sustainability efforts effectively.",
  },
  "executive-talk": {
    name: "Executive Talk",
    description: "An interview service for executives to showcase their company's vision and business direction.",
  },
  "online-investor-relations": {
    name: "Online Investor Relations",
    description: "An online investor relations service that enables publicly listed companies to effectively communicate their financial information and corporate governance practices to investors and stakeholders through their official websites.",
  },
  "seo-sem": {
    name: "SEO/SEM Services",
    alternateName: "Seo Sem",
    description: "ShareInvestor (Thailand)'s SEO and SEM services enhance your online visibility and drive meaningful results.",
  },
};

// SSR-safe metadata fetch (same pattern as pages/home.vue) — populates
// title/description before render, so SSR HTML has real values instead of
// empty ones. The client-only fetchPageContent below (which also parses
// shortcodes) reuses this same response instead of fetching again.
await store.fetchPageMeta(route.path);

const pageTitle = computed(() => store.title);
const pageDescription = computed(() => store.description);
const isLoading = computed(() => store.loading);
const pagePath = computed(() => store.path || route.path.replace(/^\/(en|th)/, ""));
const pageContent = ref("");

// No reliable per-page title exists from the CMS for these generic pages
// (alternate_title/title is brand-only), so derive a readable H1 from the
// last URL segment instead — synchronous, no extra fetch needed for SSR.
const h1Text = computed(() => {
  const segments = route.path.split("/").filter(Boolean);
  const lastSegment = segments[segments.length - 1] || "";
  return decodeURIComponent(lastSegment)
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
});


const pageUrl = computed(() => `${siteUrl}${route.path}`);
const serviceData = computed(() => SERVICE_DATA[route.params.slug?.[route.params.slug.length - 1]]);

useHead(() => {
  // Crumb labels come from the CMS breadcrumbs so they match the real menu
  // wording ("SEO/SEM", "ESG Microsite") rather than a title-cased slug.
  // URLs are derived from route.path instead: "Our Service" has no page of
  // its own with content, so both the CMS and the header menu point it at
  // its first child (online-investor-relations). The hierarchical URL is
  // used here to keep the trail a real ancestor chain.
  const categoryPath = route.path.replace(/\/[^/]+$/, "");
  const crumbs = store.breadcrumbs;
  const categoryName = crumbs[0]?.title || "Our Service";
  const pageName = (crumbs.length >= 2 && crumbs[crumbs.length - 1]?.title) || h1Text.value;

  const breadcrumbItems = [
    { name: "Home", url: `${siteUrl}/` },
    { name: categoryName, url: `${siteUrl}${categoryPath}` },
    { name: pageName, url: pageUrl.value },
  ];

  const scripts = [
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify(useBreadcrumbSchema(breadcrumbItems, pageUrl.value)),
    },
  ];

  if (serviceData.value) {
    scripts.push({
      type: "application/ld+json",
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Service",
        "@id": `${pageUrl.value}#service`,
        name: serviceData.value.name,
        ...(serviceData.value.alternateName ? { alternateName: serviceData.value.alternateName } : {}),
        serviceType: serviceData.value.name,
        description: serviceData.value.description,
        url: pageUrl.value,
        areaServed: { "@type": "Country", name: "Thailand" },
        provider: {
          "@type": "Organization",
          "@id": `${siteUrl}/#organization`,
          name: "ShareInvestor Thailand",
          url: siteUrl,
        },
      }),
    });
  }

  return { script: scripts };
});

const loadScript = (src, isAsync = true, isDefer = true) => {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = isAsync;
    script.defer = isDefer;
    script.onload = resolve;
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
    document.head.appendChild(script);
  });
};

const injectCustomHead = async (customHead) => {
  const head = document.head;
  const headFragment = document.createRange().createContextualFragment(customHead);
  head.appendChild(headFragment);
};

const injectCustomFooter = async (customFooter) => {
  const body = document.body;
  const footerFragment = document.createRange().createContextualFragment(customFooter);

  // ดึง script จาก customFooter
  const scripts = footerFragment.querySelectorAll('script[src]');
  const scriptUrls = Array.from(scripts).map((script) => script.src);

  // Append footer HTML ยกเว้น script
  scripts.forEach((script) => script.remove());
  body.appendChild(footerFragment);

  // โหลด script ทีละตัว
  for (const src of scriptUrls) {
    try {
      await loadScript(src);
    } catch (error) {
      console.error(`Failed to load script: ${src}`, error);
    }
  }
};

// Fetch the page content based on the route's slug
const fetchPageContent = async () => {
  try {
    // const slug = Array.isArray(route.params.slug)
    //   ? route.params.slug.join("/")
    //   : route.params.slug;

    const path = route.path;
    await store.fetchPageContent(path);
    pageContent.value = store.content;

    if (store.content.custom_head) {
      injectCustomHead(store.content.custom_head);
    }
    if (store.content.custom_footer) {
      injectCustomFooter(store.content.custom_footer);
    }

  } catch (error) {
    console.error("Error fetching page content:", error);
  }
};

// onMounted(fetchPageContent);
const onScroll = () => {
  const scrollElement = document.getElementById("scroll-element");
  const sectionContent = document.getElementById("sectionFade");
  const sectionContentBottom = sectionContent?.getBoundingClientRect().bottom || 0;

  // const sectionLeave = document.getElementById("sectionLeave");
  // const sectionLeaveTop = sectionLeave?.getBoundingClientRect().bottom || 0;

  const windowHeight = window.innerHeight;

    if (sectionContentBottom <= windowHeight) {
      scrollElement?.classList.add("is-opacity");
      document.body.classList.add("is-black");
    } else {
     scrollElement?.classList.remove("is-opacity");
      document.body.classList.remove("is-black");
    }

    // if (sectionLeaveTop <= windowHeight) {
    //   document.body.classList.remove("is-black");
    // } 

    if (window.scrollY < 100) {
      document.body.classList.remove("is-black");
    }
 
};

onMounted(() => {
  if (document.body) {
    window.addEventListener("scroll", onScroll);
  }
  fetchPageContent();
  
});

onBeforeUnmount(() => {
  window.removeEventListener("scroll", onScroll);
});

</script>

<style lang="scss">

@import "@/assets/scss/app";
@import "@/assets/scss/components/circle-link";
@import "@/assets/scss/pages/service/index";

// .section__heading {
//   @include media-breakpoint-down(lg) {
//     margin-bottom: 1rem;
//   }
// }

// .section__detail-wrapper {
//   @include media-breakpoint-down(lg) {
//     margin: 3.25rem 0 2.5rem 0;
//   }
// }

// .card {
//   &.card--item-information {
//     .card__title {
//       min-height: unset;
//     }
//   }
// }

// .section__bg-bottom-small {
//   bottom: 0 !important;
// }

.is-opacity{
  opacity: 0;
}
</style>
